import { useBoolean } from '@fluentui/react-hooks';
import { useCallback, useMemo } from 'react';

import { useQueryAllWorkspaces, useQueryTags } from '../../authz/authz.hooks';
import type { FilterState } from '../../components/PageFilters/types';
import { WorkspacePageControls } from './components/WorkspacePageControls/WorkspacePageControls';
import { useOrderBy } from './hooks/useOrderBy';
import { useWorkspaceFiltering } from './hooks/useWorkspaceFiltering';
import { WorkspacesList } from './WorkspacesList';

export const AdminWorkspaces = () => {
  const { filters, setFilters, searchText, handleSearchChange, aipFilter, resetFilters } = useWorkspaceFiltering();
  const { orderBy, setOrderBy } = useOrderBy();
  const [showDeletedAndArchived, { toggle: toggleShowDeletedAndArchived }] = useBoolean(false);

  const orderByString = useMemo(() => {
    if (!orderBy.key) return undefined;
    return orderBy.descending ? `${orderBy.key} desc` : orderBy.key;
  }, [orderBy]);

  const workspacesQuery = useQueryAllWorkspaces(
    {
      filter: aipFilter,
      orderBy: orderByString,
      showDeleted: showDeletedAndArchived,
      showArchived: showDeletedAndArchived,
    },
    {
      select: (data) => {
        if (!data?.pages) return { pages: [], pageParams: [] };

        const workspacesFromAllPages = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: workspacesFromAllPages, pageParams: data.pageParams };
      },
    }
  );

  const tagsQuery = useQueryTags(
    {},
    {
      select: (data) => {
        if (!data?.pages) {
          return { pages: [], pageParams: [] };
        }
        const allTags = data.pages.flatMap((page) => page.tags || []);
        return { pages: allTags, pageParams: data.pageParams };
      },
    }
  );

  const tags = tagsQuery.data?.pages;

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, FilterState<unknown>>) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const topPanelSlot = (
    <WorkspacePageControls
      filters={filters}
      onFiltersChange={handleFiltersChange}
      onClearFilters={resetFilters}
      availableTags={tags}
      showDeleted={showDeletedAndArchived}
      onToggleShowDeleted={toggleShowDeletedAndArchived}
    />
  );

  return (
    <WorkspacesList
      workspacesQuery={workspacesQuery}
      tagsQuery={tagsQuery}
      searchText={searchText}
      onSearchChange={handleSearchChange}
      filters={filters}
      topPanelSlot={topPanelSlot}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
    />
  );
};
